import React from "react";
import {
  BusinessRulesListDetails,
  LocksInCategory,
  ProductSequenceData,
  ProductPublishedData,
  ContentSlot,
  WatchedProductListData,
} from "../store/product-list/ProductListTypes";
import {
  Product,
  ProductAttributeValue,
  ProductSwatchType,
  VariantAttribute,
  ChildProduct,
  CategoryType,
  ProductIdCodes,
} from "../store/product/ProductTypes";
import { ColorManagementProductData } from "../store/color-management/ColorManagementTypes";
import {
  LeadingImageVariationAttributesData,
  PreSelectedColorType,
} from "../store/leading-image/LeadingImageTypes";
import { LockedProducts } from "store/remove-product-categories/RemoveProductCategoriesTypes";
import AppEnv from "./AppEnv";
import { ProductColorNode } from "store/product/ProductReducers";
import { setLockedUnlockedProducts } from "store/product-list/ProductListActions";
import { FreqBoughtProductData } from "../store/recommendations/RecommendationType";
import { CURRENCY_MAP } from "./Constants";
import { allDefined, isDefined } from "./ValidationUtils";

export const isProductPublished = (product: ProductPublishedData): boolean => {
  const productIsPublished = product.isPublished;
  const localeIsPublished =
    product.descriptions && product.descriptions.length > 0
      ? product.descriptions[0].isPublished
      : true;

  return productIsPublished && localeIsPublished;
};
export const isProductPublishedSFCC = (
  product?: ProductPublishedData,
): boolean => {
  const productIsPublished = product?.isPublished ?? false;

  return productIsPublished;
};

export const isProductPublishedSFCCStore = (
  product: ProductPublishedData,
  storeId: string,
): boolean | null => {
  if ((product.overrides?.length ?? 0) > 0) {
    return (
      product.overrides?.filter((item) => item.storeId === storeId)[0]
        ?.isPublished ?? null
    );
  }
  return null;
};

export const getProductName = (
  product: Product,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const description =
    product.descriptions?.find((desc) => desc.localeCode === localeCode) ||
    product.descriptions?.find((desc) => desc.localeCode === defaultLocaleCode);
  return description
    ? `${description.name} (${product.code})`
    : product.name
      ? `${product.name} (${product.code})`
      : product.code
        ? product.code
        : product.productId;
};

export const getProductImageSFCC = (
  product: Product | ChildProduct,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const description =
    product.descriptions?.find((desc) => desc.localeCode === localeCode) ||
    product.descriptions?.find((desc) => desc.localeCode === defaultLocaleCode);
  return description ? description.thumbnail : "";
};

export const getAttributeDisplayNameSFCC = (
  productAttributeValue: ProductAttributeValue,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    productAttributeValue.attributeValue.attribute.descriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    productAttributeValue.attributeValue.attribute.descriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const attributeDisplayName = desc
    ? desc.displayName
    : productAttributeValue.attributeValue.attribute.code
      ? productAttributeValue.attributeValue.attribute.code
      : productAttributeValue.attributeValue.attribute.attributeId;

  return attributeDisplayName;
};

export const getAttributeValueDisplayNameSFCC = (
  productAttributeValue: ProductAttributeValue,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    productAttributeValue.attributeValue.descriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    productAttributeValue.attributeValue.descriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const attributeValueDisplayName = desc
    ? desc.displayValue
    : productAttributeValue.attributeValue.code
      ? productAttributeValue.attributeValue.code
      : productAttributeValue.attributeValueId;

  return attributeValueDisplayName;
};

export const getVariantAttributeDisplayName = (
  attribute: VariantAttribute,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    attribute.descriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    attribute.descriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const attributeDisplayName = desc
    ? desc.displayValue
    : attribute.code
      ? attribute.code
      : attribute.attributeId;
  return attributeDisplayName;
};

export const getVariantAttributeValueDisplayName = (
  attributeValue: VariantAttribute,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    attributeValue.descriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    attributeValue.descriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const attributeValueDisplayName = desc
    ? desc.displayValue
    : attributeValue.code
      ? attributeValue.code
      : attributeValue.attributeId;
  return attributeValueDisplayName;
};

export const getAttributeValueSequenceSFCC = (
  productAttributeValue: ProductAttributeValue,
  localeCode: string,
  defaultLocaleCode: string,
): number => {
  const desc =
    productAttributeValue.attributeValue.descriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    productAttributeValue.attributeValue.descriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  return desc ? desc.sequence : 0;
};

export const getProductCategoryNameSFCC = (
  categoryObj: CategoryType,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    categoryObj.translations.find(
      (translation) => translation.localCode === localeCode,
    ) ||
    categoryObj.translations.find(
      (translation) => translation.localCode === defaultLocaleCode,
    );
  const productCategoryName = desc ? desc.name : categoryObj.name;
  return productCategoryName;
};

export const getProductSwatchColorSFCC = (
  swatch: ProductSwatchType,
  localeCode: string,
  defaultLocaleCode: string,
): string => {
  const desc =
    swatch.colorDescriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    swatch.colorDescriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const swatchColor = desc ? desc.displayValue : "";
  return swatchColor;
};

export const getProductSwatchImageSFCC = (
  swatch: ProductSwatchType,
  localeCode: string,
  defaultLocaleCode: string,
): string | undefined => {
  const desc =
    swatch.colorDescriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    swatch.colorDescriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const thumbnail = desc?.thumbnail;
  return thumbnail;
};

export const getProductSwatchChildProductImageSFCC = (
  swatch: ProductSwatchType,
  localeCode: string,
  defaultLocaleCode: string,
): string | undefined => {
  const desc =
    swatch.childProductDescriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    swatch.childProductDescriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const thumbnail = desc?.thumbnail;
  return thumbnail;
};

export const getProductSwatchLocaleSequenceSFCC = (
  swatch: ProductSwatchType,
  localeCode: string,
  defaultLocaleCode: string,
): number => {
  const desc =
    swatch.colorDescriptions.find(
      (description) => description.localeCode === localeCode,
    ) ||
    swatch.colorDescriptions.find(
      (description) => description.localeCode === defaultLocaleCode,
    );
  const sequence = desc?.sequence;
  return sequence || 0;
};

export const calculateStartEndDate = (
  sortingRule: BusinessRulesListDetails,
): { startDate: string; endDate: string } => {
  const dateObj = new Date();
  const month = dateObj.getUTCMonth() + 1;
  const day = dateObj.getUTCDate();
  const year = dateObj.getUTCFullYear();

  const formatDate = (date: number) => (date < 10 ? "0" + date : date);

  let startDate;
  let endDate = `${year}${formatDate(month)}${formatDate(day)}`;

  switch (sortingRule.ruleType) {
    case "MCS":
      const wsCriteria = sortingRule.criteria.find((c) => c.ruleType === "WS");
      startDate = wsCriteria ? wsCriteria.dateRange : "last_14_days";
      break;
    default:
      startDate = sortingRule.dateRange || "last_14_days";
      break;
  }

  const calculateDate = (days: number) => {
    const newDateObj = new Date(dateObj);
    newDateObj.setDate(dateObj.getDate() - days);
    const newMonth = newDateObj.getUTCMonth() + 1;
    const newDay = newDateObj.getUTCDate();
    const newYear = newDateObj.getUTCFullYear();
    return `${newYear}${formatDate(newMonth)}${formatDate(newDay)}`;
  };

  switch (startDate) {
    case "last_7_days":
      startDate = calculateDate(7);
      break;
    case "last_14_days":
      startDate = calculateDate(14);
      break;
    case "last_30_days":
      startDate = calculateDate(30);
      break;
    case "last_90_days":
      startDate = calculateDate(90);
      break;
    case "last_180_days":
      startDate = calculateDate(180);
      break;
    default:
      startDate = calculateDate(14);
      break;
  }

  return { startDate, endDate };
};

export const getProductLeadingColorImageSFCC = (
  productData: Product | ColorManagementProductData | FreqBoughtProductData,
  currentCategoryId: string,
) => {
  let index;
  if (productData.categorySpecific && productData?.colors) {
    const productCategorySpecificData = productData?.categorySpecific.filter(
      (categoryObj) => {
        return (
          Object.values(categoryObj).includes(currentCategoryId) &&
          categoryObj.leadingColorId
        );
      },
    );
    index = productData?.colors?.findIndex(
      (color) =>
        productCategorySpecificData.length > 0 &&
        color.colorId === productCategorySpecificData[0].leadingColorId &&
        color.isPublished,
    );
    return index >= 0 ? productData?.colors[index].thumbnail : "";
  }
};

export const getProductLeadingImageOnInitialLoad = (
  productData:
    | Product
    | FreqBoughtProductData
    | ColorManagementProductData
    | null,
  currentCategoryId: string,
) => {
  let leadingImage: string = "";
  if (
    productData &&
    productData?.categorySpecific &&
    productData?.categorySpecific.length > 0 &&
    productData?.colors
  ) {
    productData?.categorySpecific?.forEach((categoryObj) => {
      if (
        Object.values(categoryObj).includes(currentCategoryId) &&
        categoryObj.leadingImageId
      ) {
        if (productData && productData?.imageGroups) {
          for (const imageGroup of productData.imageGroups) {
            if (!leadingImage) {
              const index = imageGroup.images?.findIndex(
                (image) => image.imageId === categoryObj?.leadingImageId,
              );
              if (
                index >= 0 &&
                productData?.colors &&
                imageGroup?.variationAttributes?.some(
                  (attr) =>
                    attr.attributeId ===
                      productData?.colorVariationAttributeId &&
                    attr.values?.some(
                      (attrValue) =>
                        productData?.colors?.find(
                          (color) => color.colorId === attrValue.value,
                        )?.isPublished,
                    ),
                )
              ) {
                leadingImage = imageGroup?.images?.[index]?.thumbnail;
                return leadingImage;
              }
            }
          }
        }
      }
    });
  }
  return leadingImage;
};

export const getProductColorSFCC = (
  baseProductUpdatedLeadingColor: string,
  productData: Product | null,
  currentCategoryId: string,
  variationAttributes: LeadingImageVariationAttributesData[],
) => {
  let preSelectedColor: PreSelectedColorType = {};
  const colorAttrId = productData?.colorVariationAttributeId;
  if (colorAttrId === "color") {
    if (
      baseProductUpdatedLeadingColor &&
      baseProductUpdatedLeadingColor !== "" &&
      productData?.colors?.find(
        (color) => color.name === baseProductUpdatedLeadingColor,
      )?.isPublished
    ) {
      preSelectedColor = {
        [colorAttrId]: baseProductUpdatedLeadingColor,
      };
      return preSelectedColor;
    } else {
      const productCategorySpecificData = productData?.categorySpecific?.filter(
        (categoryObj) => {
          return (
            Object.values(categoryObj).includes(currentCategoryId) &&
            categoryObj.leadingColorId
          );
        },
      );
      const leadingColor =
        productCategorySpecificData && productCategorySpecificData?.length > 0
          ? productCategorySpecificData[0].leadingColorId
          : "";
      const colorData = productData?.colors?.find(
        (color) => color.colorId === leadingColor,
      )?.isPublished;
      if (leadingColor && leadingColor !== "" && colorData) {
        preSelectedColor = {
          [colorAttrId]: leadingColor,
        };
        return preSelectedColor;
      } else {
        if (variationAttributes.length > 0) {
          for (const attr of variationAttributes) {
            if (attr.attributeId === "color") {
              const attrValues = attr.values
                ?.filter(
                  (value) =>
                    productData?.colors?.find(
                      (color) => color.name === value.name,
                    )?.isPublished,
                )
                .find((value) => value.isAssigned === true);
              if (attrValues) {
                preSelectedColor = {
                  [colorAttrId]: attrValues.value,
                };
                return preSelectedColor;
              }
            }
          }
        }
      }
    }
  }
};

export const isAbsolutePath = (imagePath?: string | null) => {
  try {
    const path = imagePath ?? "";
    new URL(path);
    return true; // It's a valid URL (which is considered absolute)
  } catch (error) {
    return false; // It's not a valid URL (could be a relative path)
  }
};

export const isListPriceGreaterThanPrice = (
  price: string | null,
  priceRange: number[] | null,
) => {
  let isGreaterListPrice = false;
  if (price && priceRange) {
    //Check if high Price exists
    if (priceRange[1]) {
      //Verifying List Price greater than high Price
      if (Number(price) > priceRange[1]) isGreaterListPrice = true;
    } else if (priceRange[0]) {
      //If high price does not exists checking with low price
      if (Number(price) > priceRange[0]) isGreaterListPrice = true;
    }
  }

  return isGreaterListPrice;
};

export const swapItemsInArray = (
  productIds: string[],
  selectedTile: string,
  baseProduct: string,
) => {
  const editSequenceIds = [...productIds];
  const selectedTileIndex = editSequenceIds.indexOf(selectedTile);
  const baseProductIndex = editSequenceIds.indexOf(baseProduct);

  if (
    selectedTileIndex !== -1 &&
    baseProductIndex !== -1 &&
    selectedTileIndex !== baseProductIndex
  ) {
    [editSequenceIds[selectedTileIndex], editSequenceIds[baseProductIndex]] = [
      editSequenceIds[baseProductIndex],
      editSequenceIds[selectedTileIndex],
    ];
    const newBaseProductIndex = editSequenceIds.indexOf(baseProduct);
    editSequenceIds.splice(newBaseProductIndex, 1);
  }

  return editSequenceIds;
};

export const getPrice = (
  lowPrice?: number,
  highPrice?: number,
  currencySymbol?: string,
) => {
  const price =
    lowPrice && highPrice
      ? lowPrice === highPrice
        ? `${currencySymbol}${lowPrice}`
        : `${currencySymbol}${lowPrice} - ${currencySymbol}${highPrice}`
      : lowPrice
        ? `${currencySymbol}${lowPrice}`
        : "";
  return price;
};

export function calculateBadges(
  mainBadges,
  productBadgeAssignments,
  productId,
) {
  // Validate input arrays
  if (!Array.isArray(mainBadges) || mainBadges.length === 0) {
    return null;
  }

  if (
    !Array.isArray(productBadgeAssignments) ||
    productBadgeAssignments.length === 0
  ) {
    return null;
  }

  // Find the matching product by productId
  const product = productBadgeAssignments.find(
    (item) => item.productId === productId,
  );

  if (
    !product ||
    !Array.isArray(product.badges) ||
    product.badges.length === 0
  ) {
    return null;
  }

  // Flatten and filter valid badge IDs
  const badgeIds = product.badges
    .flatMap((badge) => badge.badgeId)
    .filter((id) => mainBadges.some((badge) => badge.badgeId === id));

  if (badgeIds.length === 0) {
    console.warn(`No matching badge found for productId: ${productId}`);
    return null;
  }
  // Find the badge with the highest precedence
  const badgeId = badgeIds.reduce((prev, curr) => {
    const prevBadge = mainBadges.find((badge) => badge.badgeId === prev);
    const currBadge = mainBadges.find((badge) => badge.badgeId === curr);
    return prevBadge &&
      currBadge &&
      +prevBadge.precedence > +currBadge.precedence
      ? prev
      : curr;
  }, null);

  return badgeId;
}

export const getProductModalTitle = (product: Product | null) => {
  return product ? `${product.name} (${product.code})` : "";
};

export const getProductCode = (
  productIdCodes: ProductIdCodes[],
  productId: string,
) => {
  return productIdCodes.find((p) => p.productId === productId)?.code;
};

export const filterLockedProducts = (
  lockedProducts: LockedProducts[],
  productIds: string[],
): LockedProducts[] => {
  return lockedProducts.filter((product) =>
    productIds.includes(product.productId),
  );
};

export const isPinnedProduct = (
  storedPinnedProductIds: string[],
  storedUnpinnedProductIds: string[],
  pinnedProductIds: string[],
  productId: string,
) => {
  return (
    (storedPinnedProductIds?.includes(productId) ||
      pinnedProductIds?.includes(productId)) &&
    !storedUnpinnedProductIds?.includes(productId)
  );
};

export const getPinnedProductsOutOfPosition = (
  productsList: string[],
  pinnedProducts: string[],
  lockedProductsInCategory: LocksInCategory[],
  lockedProductsOutOfPosition: string[],
): string[] => {
  const lockedPositions = new Map(
    lockedProductsInCategory
      .filter((lock) => !lockedProductsOutOfPosition.includes(lock.productId))
      .map((lock) => [lock.productId, lock.position]),
  );

  // Create a map of current positions for quick lookup (0-based)
  const currentPositions = new Map(
    productsList.map((productId, index) => [productId, index]),
  );

  // Check if the first product is unlocked or not pinned
  const firstProductId = productsList[0];
  const isFirstProductLocked = lockedPositions.has(firstProductId);
  const isFirstProductPinned = pinnedProducts.includes(firstProductId);

  // If first product is unlocked and not pinned, all pinned products are out of position
  if (!isFirstProductLocked && !isFirstProductPinned) {
    return pinnedProducts;
  }

  // Calculate the expected position for each pinned product
  const outOfPositionProducts = pinnedProducts.filter((productId) => {
    const pinnedIndex = pinnedProducts.indexOf(productId);
    const currentIndex = currentPositions.get(productId) ?? -1;

    // Count how many locked products come before this pinned product's expected position
    let adjustedIndex = pinnedIndex;
    for (const [lockedProductId, lockedPosition] of lockedPositions) {
      // Skip if this locked product is not in the current product list
      if (!currentPositions.has(lockedProductId)) continue;

      // Convert locked position from 1-based to 0-based for comparison
      const lockedPositionZeroBased = lockedPosition - 1;

      // If the locked product's position is before or at our target position, shift our target
      if (lockedPositionZeroBased <= adjustedIndex) {
        adjustedIndex++;
      }
    }
    return currentIndex !== adjustedIndex;
  });

  return outOfPositionProducts;
};

export const reorderProductsList = (
  productsList: ProductSequenceData[],
  lockedProductsInCategory: LocksInCategory[],
  pinnedProducts: string[],
) => {
  const sortedLockedProducts = sortLockedProductsByPosition(
    lockedProductsInCategory,
  );
  // Create sets for faster lookups
  const lockedProductIds = new Set(
    sortedLockedProducts.map((lock) => lock.productId),
  );
  const pinnedProductIds = new Set(pinnedProducts);

  // Separate products into categories
  const lockedProducts = productsList.filter((product) =>
    lockedProductIds.has(product.productId),
  );
  const pinnedNonLockedProducts = pinnedProducts
    .map((pinnedId) =>
      productsList.find((product) => product.productId === pinnedId),
    )
    .filter(
      (product): product is ProductSequenceData =>
        product !== undefined && !lockedProductIds.has(product.productId),
    );
  const remainingProducts = productsList.filter(
    (product) =>
      !pinnedProductIds.has(product.productId) &&
      !lockedProductIds.has(product.productId),
  );

  // Create result array with null placeholders
  const reorderedProducts: (ProductSequenceData | null)[] = new Array(
    productsList.length,
  ).fill(null);

  // First, place locked products
  sortedLockedProducts.forEach((lock) => {
    const product = lockedProducts.find((p) => p.productId === lock.productId);
    if (product) {
      // If position is greater than array length, find the next available position
      let position = lock.position - 1;
      if (position >= reorderedProducts.length) {
        position = reorderedProducts.length - 1;
      }
      // Find the next available position if the current one is taken
      while (position >= 0 && reorderedProducts[position] !== null) {
        position--;
      }
      if (position >= 0) {
        reorderedProducts[position] = product;
      }
    }
  });

  // Find the first available position for pinned products
  let currentPosition = 0;

  // Place pinned products consecutively, only skipping locked positions
  for (const pinnedProduct of pinnedNonLockedProducts) {
    // Skip any locked positions
    while (
      currentPosition < reorderedProducts.length &&
      reorderedProducts[currentPosition] !== null
    ) {
      currentPosition++;
    }
    if (currentPosition < reorderedProducts.length) {
      reorderedProducts[currentPosition] = pinnedProduct;
      currentPosition++;
    }
  }

  // Fill remaining positions with non-pinned, non-locked products
  let remainingIndex = 0;
  for (let i = 0; i < reorderedProducts.length; i++) {
    if (
      reorderedProducts[i] === null &&
      remainingIndex < remainingProducts.length
    ) {
      reorderedProducts[i] = remainingProducts[remainingIndex];
      remainingIndex++;
    }
  }

  return reorderedProducts.filter(
    (product): product is ProductSequenceData => product !== null,
  );
};

export const getContentSlotsBeforeTheCurrentPosition = (
  contentSlots: ContentSlot[],
  currentPosition: number,
): number => {
  return contentSlots.filter((slot) => slot.position < currentPosition).length;
};

export const havePinnedProductsChanged = (
  pinnedSequenceProducts: ProductSequenceData[],
  pinnedProductsFromDB: string[],
  productSequences: ProductSequenceData[],
) => {
  // First check if they have the same products
  const pinnedSequenceIds = pinnedSequenceProducts.map((p) => p.productId);

  // If lengths are different, there's definitely a change
  if (pinnedSequenceIds.length !== pinnedProductsFromDB.length) {
    return true;
  }

  // Check if they contain exactly the same products
  const hasAllProducts = pinnedProductsFromDB.every((id) =>
    pinnedSequenceIds.includes(id),
  );

  if (!hasAllProducts) {
    return true;
  }

  // If they have the same products, check their order
  // Get current sequence of pinned products
  const currentPinnedOrder = productSequences
    .filter((p) => pinnedProductsFromDB.includes(p.productId))
    .map((p) => p.productId);

  // Compare orders
  return pinnedProductsFromDB.some(
    (id, index) => id !== currentPinnedOrder[index],
  );
};

/**
 * Checks if a product is of a specific type
 * @param productTypeCode - The type code of the product to check
 * @returns boolean indicating if the product is of the specified type
 */
export const isProductOfType = (
  productTypeCode: string | undefined,
): boolean => {
  if (!productTypeCode) return false;
  return (
    productTypeCode !== "Variation Product" &&
    productTypeCode !== "Simple Product" &&
    productTypeCode !== "Grouped Product" &&
    productTypeCode !== "Bundle Product"
  );
};

const TYPE_CODE_LABELS: Record<string, string> = {
  ProductBean: "Product",
  BundleBean: "Bundle",
  PackageBean: "Package",
  DynamicKitBean: "Dynamic Kit",
};

/**
 * Resolves the display label for a product type code.
 * @param typeCode - The raw type code from the adapter response
 * @returns The friendly label, or the original typeCode if no mapping exists
 */
export const getDisplayTypeCode = (
  typeCode: string | undefined,
): string | undefined =>
  typeCode && TYPE_CODE_LABELS[typeCode]
    ? TYPE_CODE_LABELS[typeCode]
    : typeCode;

/**
 * Sorts locked products in category by their position in ascending order
 * @param lockedProductsInCategory - Array of locked products with their positions
 * @returns Sorted array of locked products by position
 */
export const sortLockedProductsByPosition = (
  lockedProductsInCategory: LocksInCategory[],
): LocksInCategory[] => {
  return [...lockedProductsInCategory].sort((a, b) => a.position - b.position);
};

export const isProductInWatchlist = (
  watchedProductsList: WatchedProductListData[],
  productId: string,
) => {
  if (watchedProductsList && watchedProductsList.length) {
    return (
      watchedProductsList.findIndex(
        (product) => product.productId === productId,
      ) !== -1
    );
  }
  return false;
};

export const getAdjustedIndexWithContentSlots = (
  initialIndex: number,
  contentSlots: ContentSlot[],
): number => {
  let adjustedIndex = initialIndex;
  let keepAdjusting = true;

  while (keepAdjusting) {
    const currentIndex = adjustedIndex;
    const slotsBeforeIndex =
      contentSlots?.filter((slot) => {
        const slotPosition = slot.position - 1; // Convert to 0-based index
        return slotPosition <= currentIndex;
      }).length ?? 0;

    const newAdjustedIndex = initialIndex + slotsBeforeIndex;

    if (newAdjustedIndex === adjustedIndex) {
      keepAdjusting = false;
    }
    adjustedIndex = newAdjustedIndex;
  }

  return adjustedIndex;
};

// Helper function to calculate product ID progress
export const calculateProductIdsProgress = (
  productIds: string[],
  total: number,
): number => {
  return Math.min(15, Math.floor((productIds.length / total) * 15));
};

// Helper function to calculate combined progress with product ID progress
export const calculateCombinedProgress = (
  finalProgress: number,
  isFetchingCategoryProductIds: boolean,
  productIds: string[],
  total: number,
): number => {
  if (!isFetchingCategoryProductIds) {
    const productIdsProgress = calculateProductIdsProgress(productIds, total);
    const remainingProgress = Math.floor((finalProgress * 85) / 100);
    const combinedProgress = productIdsProgress + remainingProgress;

    // If all product details are loaded, ensure we reach 100%
    if (finalProgress === 100) {
      return 100;
    }

    return Math.min(100, combinedProgress);
  }
  return finalProgress;
};

// Helper function to calculate progress with caching
export const calculateProgressWithCache = (
  loadedItems: number,
  totalItemsToLoad: number,
  cacheKey: string,
  progressCache: Record<string, number>,
  isFetchingCategoryProductIds: boolean,
  productIds: string[],
  total: number,
): number => {
  const calculatedProgress =
    totalItemsToLoad > 0
      ? Math.min(
          100,
          Math.max(0, Math.floor((loadedItems / totalItemsToLoad) * 100)),
        )
      : 100;

  // Use cache to prevent progress from going backwards
  const previousProgress = progressCache?.[cacheKey] || 0;
  const finalProgress =
    calculatedProgress === 100
      ? 100
      : Math.max(calculatedProgress, previousProgress);

  return calculateCombinedProgress(
    finalProgress,
    isFetchingCategoryProductIds,
    productIds,
    total,
  );
};

// Helper function for V1 progress calculation
export const calculateV1Progress = (
  totalItemsToLoad: number,
  loadedItems: number,
  cacheKey: string,
  progressCache: Record<string, number>,
): number => {
  const calculatedProgress =
    totalItemsToLoad > 0
      ? Math.min(
          100,
          Math.max(0, Math.floor((loadedItems / totalItemsToLoad) * 100)),
        )
      : 100;

  // Use cache to prevent progress from going backwards
  const previousProgress = progressCache?.[cacheKey] || 0;
  const finalProgress =
    calculatedProgress === 100
      ? 100
      : Math.max(calculatedProgress, previousProgress);

  return finalProgress;
};

export const getTotalItems = (
  isFetchedBySearch: boolean,
  productsBySearch: ProductSequenceData[],
  sequenceMode: number,
  products: ProductSequenceData[],
  editProducts: ProductSequenceData[],
) => {
  const totalItems = isFetchedBySearch
    ? productsBySearch
    : sequenceMode === 1
      ? products
      : editProducts;

  return totalItems;
};

export const getCurrentPageProductIds = (
  contentSlots: ContentSlot[],
  currentPage: number,
  isFetchedBySearch: boolean,
  productsBySearch: ProductSequenceData[],
  sequenceMode: number,
  products: ProductSequenceData[],
  editProducts: ProductSequenceData[],
  sortedProducts?: string[],
) => {
  const pageSize = AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24;
  const allProducts =
    sortedProducts ??
    getTotalItems(
      isFetchedBySearch,
      productsBySearch,
      sequenceMode,
      products,
      editProducts,
    );

  // Calculate total items including content slots
  const totalItems = allProducts.length + (contentSlots?.length || 0);

  // Calculate the start and end positions for this page
  const start = (currentPage - 1) * pageSize;
  const end = Math.min(start + pageSize, totalItems);

  // Get content slots that should appear in this page
  const pageSlots =
    contentSlots?.filter((slot) => {
      const position = slot.position - 1; // Convert to 0-based index
      return position >= start && position < end;
    }) || [];

  // Calculate how many product slots are available on this page
  const availableProductSlots = pageSize - pageSlots.length;

  // Calculate the product range for this page
  let productStart = start;
  let productEnd = start + availableProductSlots;

  // Adjust for content slots in previous pages
  const previousSlots =
    contentSlots?.filter((slot) => {
      const position = slot.position - 1;
      return position < start;
    }) || [];

  // Adjust the product range based on previous content slots
  productStart -= previousSlots.length;
  productEnd -= previousSlots.length;

  // Ensure we don't exceed the total number of products
  productEnd = Math.min(productEnd, allProducts.length);
  productStart = Math.min(productStart, allProducts.length);

  // Get the products for this page
  const pageProducts = allProducts.slice(productStart, productEnd);

  return sortedProducts
    ? pageProducts
    : pageProducts.map((product) => product.productId);
};

export const getProductsHavingColors = (
  products: string[],
  productColorsData: ProductColorNode,
): boolean => {
  if (!products?.length) return false;
  return products.some((productId) => {
    const productColors = productColorsData[productId];
    return (
      productColors && productColors.colors && productColors.colors.length > 0
    );
  });
};

export const getColorOptions = (
  productColorsData: ProductColorNode,
  products: string[],
): { label: string; value: string }[] => {
  const allColors = products
    .map((productId) => {
      const productColors = productColorsData[productId];
      return productColors &&
        productColors.colors &&
        productColors.colors.length > 0
        ? productColors.colors
            .filter((color) => color.isPublished)
            .map((color) => ({
              label: color.name,
              value: color.name,
            }))
        : null;
    })
    ?.filter((color) => color !== null)
    .flat();

  // Remove duplicates based on label
  const uniqueColors = allColors?.filter(
    (color, index, self) =>
      index === self.findIndex((c) => c.label === color.label),
  );

  // Sort alphabetically by label
  return uniqueColors?.sort((a, b) => a.label.localeCompare(b.label));
};

export const getProductsColorMap = (
  productColorsData: ProductColorNode,
  products: string[],
): Record<string, Record<string, string>[]> => {
  const colorIdsMap: Record<string, Record<string, string>[]> = {};

  products?.forEach((productId) => {
    const productColors = productColorsData[productId];
    if (
      productColors &&
      productColors.colors &&
      productColors.colors.length > 0
    ) {
      colorIdsMap[productId] = productColors.colors
        .map((color) =>
          color.isPublished
            ? {
                [color.name]: color.colorId,
              }
            : null,
        )
        .filter((color) => color !== null);
    }
  });

  return colorIdsMap;
};

export const getCurrentPageProductsHavingLeadingColor = (
  productColorsData: ProductColorNode,
  products: string[],
  currentCategoryId: string,
): { productId: string; colorId: null }[] => {
  const productIds =
    products
      ?.map((productId) => {
        const productColors = productColorsData[productId];
        return productColors &&
          productColors.categorySpecific &&
          productColors.categorySpecific.length > 0 &&
          productColors.categorySpecific.find(
            (category) => category.categoryId === currentCategoryId,
          )?.leadingColorId
          ? productId
          : null;
      })
      ?.filter((productId) => productId !== null) ?? [];

  return productIds.map((productId) => ({ productId, colorId: null }));
};

export const updatePositionsToProducts = (
  productIds: string[],
  productPositions: ProductSequenceData[],
) => {
  const positionedProducts: ProductSequenceData[] = [];
  const unpositionedProducts: ProductSequenceData[] = [];

  // Separate positioned and unpositioned products
  productPositions.forEach((product) => {
    if (productIds.includes(product.productId)) {
      positionedProducts.push(product);
    } else {
      unpositionedProducts.push(product);
    }
  });

  // Assign position numbers to positioned products (starting from 1)
  const updatedPositionedProducts = positionedProducts.map(
    (product, index) => ({
      ...product,
      position: index + 1,
    }),
  );

  // Keep unpositioned products without position numbers
  const updatedUnpositionedProducts = unpositionedProducts.map((product) => ({
    ...product,
    position: undefined,
  }));

  return [...updatedPositionedProducts, ...updatedUnpositionedProducts];
};

/**
 * Handles undefined products and moves locked products to appropriate positions
 * @param productSequenceList - The current sequence of products
 * @param positionedProductIds - Array of product IDs that are currently positioned
 * @param locksInCategory - Array of locked products with their positions
 * @returns Object containing updated sequence, positioned IDs, and unlock actions
 */
export const handleUndefinedProductsAndLocks = (
  productSequenceList: ProductSequenceData[],
  positionedProductIds: string[],
  locksInCategory: LocksInCategory[],
): {
  updatedSequence: ProductSequenceData[];
  updatedPositionedIds: string[];
  unlockActions: Array<{
    productId: string;
    catalogId: string;
    categoryId: string;
    position: number;
    isLocked: false;
  }>;
} => {
  const updatedSequence = [...productSequenceList];
  const updatedPositionedIds = [...positionedProductIds];
  const unlockActions: Array<{
    productId: string;
    catalogId: string;
    categoryId: string;
    position: number;
    isLocked: false;
  }> = [];

  // Helper function to move locked products and unlock them
  const moveAndUnlockProducts = (
    lockedProducts: Array<{ product: ProductSequenceData; index: number }>,
    insertIndex: number,
  ) => {
    if (lockedProducts.length === 0) return;

    // Remove locked products from their current positions
    lockedProducts.forEach(({ product }) => {
      const currentIndex = updatedSequence.findIndex(
        (p) => p?.productId === product.productId,
      );
      if (currentIndex !== -1) {
        updatedSequence.splice(currentIndex, 1);
      }
    });

    // Insert locked products at the specified index
    updatedSequence.splice(
      insertIndex,
      0,
      ...lockedProducts.map((p) => p.product),
    );

    // Remove locks for the moved products and create unlock actions
    lockedProducts.forEach(({ product }) => {
      const lockIndex = locksInCategory.findIndex(
        (lock) => lock.productId === product.productId,
      );
      if (lockIndex !== -1) {
        const removedLock = locksInCategory[lockIndex];
        locksInCategory.splice(lockIndex, 1);

        // Add to unlock actions
        unlockActions.push({
          productId: product.productId,
          catalogId: removedLock.catalogId,
          categoryId: removedLock.categoryId,
          position: removedLock.position,
          isLocked: false,
        });
      }
    });

    // Update positioned IDs to include the moved locked products
    lockedProducts.forEach(({ product }) => {
      if (!updatedPositionedIds.includes(product.productId)) {
        updatedPositionedIds.push(product.productId);
      }
    });
  };

  // Check if sequence starts with undefined products
  let startIndex = 0;
  const undefinedProductsAtStart: Array<{
    product: ProductSequenceData;
    index: number;
  }> = [];

  while (
    startIndex < updatedSequence.length &&
    updatedSequence[startIndex] &&
    !updatedPositionedIds.includes(updatedSequence[startIndex].productId)
  ) {
    undefinedProductsAtStart.push({
      product: updatedSequence[startIndex],
      index: startIndex,
    });
    startIndex++;
  }

  if (undefinedProductsAtStart.length > 0) {
    // Case 1: Sequence starts with undefined products
    const lastStartUndefinedIndex = Math.max(
      ...undefinedProductsAtStart.map((p) => p.index),
    );

    // Find locked products after the start undefined products using array methods
    const lockedProductsAfterStartUndefined = updatedSequence
      .slice(lastStartUndefinedIndex + 1)
      .map((product, index) => ({
        product,
        index: lastStartUndefinedIndex + 1 + index,
      }))
      .filter(
        ({ product }) =>
          product &&
          locksInCategory.some((lock) => lock.productId === product.productId),
      );

    // Move locked products to the beginning (before undefined products)
    moveAndUnlockProducts(lockedProductsAfterStartUndefined, 0);
  } else {
    // Case 2: Check for undefined products between positioned products
    const positionedIndices = updatedSequence
      .map((product, index) => ({ product, index }))
      .filter(
        ({ product }) =>
          product && updatedPositionedIds.includes(product.productId),
      )
      .map(({ index }) => index);

    if (positionedIndices.length > 0) {
      const minPositionedIndex = Math.min(...positionedIndices);
      const maxPositionedIndex = Math.max(...positionedIndices);

      // Find undefined products between positioned products using array methods
      const undefinedProductsInRange = updatedSequence
        .slice(minPositionedIndex, maxPositionedIndex + 1)
        .map((product, index) => ({
          product,
          index: minPositionedIndex + index,
        }))
        .filter(
          ({ product }) =>
            product && !updatedPositionedIds.includes(product.productId),
        );

      // If we have undefined products, check for locked products after them
      if (undefinedProductsInRange.length > 0) {
        const lastUndefinedIndex = Math.max(
          ...undefinedProductsInRange.map((p) => p.index),
        );

        // Find locked products after undefined products using array methods
        const lockedProductsAfterUndefined = updatedSequence
          .slice(lastUndefinedIndex + 1)
          .map((product, index) => ({
            product,
            index: lastUndefinedIndex + 1 + index,
          }))
          .filter(
            ({ product }) =>
              product &&
              locksInCategory.some(
                (lock) => lock.productId === product.productId,
              ),
          );

        // Move locked products before the first undefined product
        const firstUndefinedIndex = Math.min(
          ...undefinedProductsInRange.map((p) => p.index),
        );
        moveAndUnlockProducts(
          lockedProductsAfterUndefined,
          firstUndefinedIndex,
        );
      }
    }
  }

  return {
    updatedSequence,
    updatedPositionedIds,
    unlockActions,
  };
};

/**
 * Updates product positions in a sequence based on positioned product IDs
 * @param productSequence - The sequence of products to update
 * @param positionedProductIds - Array of product IDs that should have positions
 * @returns Updated product sequence with position properties
 */
export const updateProductSequencePositions = (
  productSequence: ProductSequenceData[],
  positionedProductIds: string[],
): ProductSequenceData[] => {
  return productSequence.map((product, index) => {
    const shouldHavePosition = positionedProductIds.includes(product.productId);
    return {
      ...product,
      position: shouldHavePosition ? index + 1 : undefined,
    };
  });
};

// Helper function to handle product sequence positioning and lock management
export const handleProductSequencePositioning = (
  productSequence: ProductSequenceData[],
  positionedProductIds: Set<string>,
  updatedLockedProductsInCategory: any[],
  dispatch: any,
) => {
  // Update position properties while preserving the sequence order
  let updatedSequence = updateProductSequencePositions(
    productSequence,
    Array.from(positionedProductIds),
  );

  // Handle undefined products and locked products using utility function
  const {
    updatedSequence: finalSequence,
    updatedPositionedIds,
    unlockActions,
  } = handleUndefinedProductsAndLocks(
    updatedSequence,
    Array.from(positionedProductIds),
    updatedLockedProductsInCategory,
  );

  // Dispatch unlock actions for products whose positions have changed
  if (unlockActions.length > 0) {
    unlockActions.forEach((unlockAction) => {
      dispatch(setLockedUnlockedProducts(unlockAction));
    });
  }

  // Update final sequence with positions based on updated positioned IDs
  const finalUpdatedSequence = updateProductSequencePositions(
    finalSequence,
    updatedPositionedIds,
  );

  return finalUpdatedSequence;
};

/**
 * Creates a handler function for managing categories displayed state using a Set
 * @param setCategoriesDisplayedProductIds - State setter function for categories displayed product IDs
 * @returns A function that updates the Set based on value and productId
 */
export const createCategoriesDisplayedHandler = (
  setCategoriesDisplayedProductIds: React.Dispatch<
    React.SetStateAction<Set<string>>
  >,
) => {
  return (value: boolean, productId: string | undefined) => {
    setCategoriesDisplayedProductIds((prev) => {
      const newSet = new Set(prev);
      if (value && productId) {
        newSet.add(productId);
      } else if (!value && productId) {
        newSet.delete(productId);
      }
      return newSet;
    });
  };
};

/**
 * Single source of truth for the price string shown on a product tile.
 *
 * `price` is the product's list price as a string ("" when absent) and
 * `priceRange` is `[low]`/`[low, high]`/`null`, matching the props
 * ProductTitleContent builds for ProductDetail.
 */
export const formatTileMainPrice = (
  price: string | null,
  priceRange: number[] | null,
  type: string | null | undefined,
  currencySymbol: string,
): string => {
  const lowPrice = priceRange ? `${currencySymbol}${priceRange[0]}` : null;
  const highPrice = priceRange ? `${currencySymbol}${priceRange[1]}` : null;
  const isLowAndHighPriceAreSame = lowPrice === highPrice;
  const hasValidPriceRange = allDefined(lowPrice, highPrice);

  if (priceRange) {
    if (type === "Grouped Product" || type === "Product Set") {
      return `Starting ${type === "Product Set" ? "from" : "at"} ${lowPrice}`;
    }
    if (hasValidPriceRange) {
      return isLowAndHighPriceAreSame
        ? `${lowPrice}`
        : `${lowPrice} - ${highPrice}`;
    }
    return `${lowPrice}`;
  }
  if (type?.includes("Bundle")) {
    return price ? `${currencySymbol}${price}` : "";
  }
  return "";
};

/**
 * Builds the tile price string directly from a product entity (the shape held
 * in the product store).
 */
export const getProductTilePriceDisplay = (
  product:
    | {
        listPrice?: number | null;
        lowPrice?: number | null;
        highPrice?: number | null;
        typeCode?: string | null;
      }
    | null
    | undefined,
  currencyCode: string,
): string => {
  const currencySymbol =
    (CURRENCY_MAP as Record<string, string>)[currencyCode] ?? "$";
  const price =
    product?.listPrice !== undefined && product?.listPrice !== null
      ? product.listPrice.toString()
      : "";
  const priceRange: number[] | null = allDefined(
    product?.lowPrice,
    product?.highPrice,
  )
    ? [Number(product?.lowPrice), Number(product?.highPrice)]
    : isDefined(product?.lowPrice)
      ? [Number(product?.lowPrice)]
      : null;
  return formatTileMainPrice(
    price,
    priceRange,
    product?.typeCode ?? null,
    currencySymbol,
  );
};
